import type { ScheduledDelivery, ScheduledDeliveryLine } from "../api/types";

/**
 * G-59 — the ONE rule for "which line did that scan or typed code mean".
 * Shared by the kiosk receiving panel and the CRM delivery page so the two
 * screens can never disagree about a match.
 *
 * A code matches a line when it equals the item's barcode (digits only, so a
 * scanner's extra characters and a typed space do not matter), the item's
 * own SKU, the shop's own issued code (G-130), or the supplier's code for
 * the item on THIS delivery's supplier (a Deco "214006,590" typed with or
 * without the comma). Case does not matter; the comma in Deco codes does
 * not either.
 */
export type DeliveryLineMatch =
  | { kind: "none" }
  | { kind: "pending"; line: ScheduledDeliveryLine }
  | { kind: "confirmed"; line: ScheduledDeliveryLine }
  | { kind: "several"; lines: ScheduledDeliveryLine[] };

const digitsOf = (value: string) => value.replace(/\D/g, "");
const loose = (value: string) => value.replace(/[\s,.-]/g, "").toUpperCase();

export function lineMatchesCode(
  line: ScheduledDeliveryLine,
  supplierId: string,
  rawCode: string,
): boolean {
  const code = rawCode.trim();
  if (!code) return false;
  const digits = digitsOf(code);
  if (digits.length >= 4 && line.item.barcode && digitsOf(line.item.barcode) === digits) {
    return true;
  }
  const wanted = loose(code);
  if (line.item.sku && loose(line.item.sku) === wanted) return true;
  // G-130: the shop's own code (G-99), as the bars say it (`IT000042`) or as a
  // person reads and types it (`ITEM-000042`) — the sticker the receiving row
  // prints must find its line when it is scanned a minute later.
  const issued = line.item.issuedBarcode;
  if (issued && (loose(issued.barcodeText) === wanted || loose(issued.code) === wanted)) {
    return true;
  }
  return line.item.supplierItemAliases.some(
    (alias) => alias.supplierId === supplierId && loose(alias.supplierSku) === wanted,
  );
}

export function findDeliveryLine(
  delivery: ScheduledDelivery,
  rawCode: string,
): DeliveryLineMatch {
  const matches = delivery.lines.filter((line) =>
    lineMatchesCode(line, delivery.supplier.id, rawCode),
  );
  if (matches.length === 0) return { kind: "none" };
  const pending = matches.filter((line) => line.status === "PENDING");
  if (pending.length === 1) return { kind: "pending", line: pending[0] };
  if (pending.length > 1) return { kind: "several", lines: pending };
  // Every match is already confirmed — say which and when.
  return { kind: "confirmed", line: matches[0] };
}

/** The found line first, everything else in its original order. */
export function linesWithFoundFirst(
  lines: ScheduledDeliveryLine[],
  foundLineId: string | null,
): ScheduledDeliveryLine[] {
  if (!foundLineId) return lines;
  const found = lines.find((line) => line.id === foundLineId);
  if (!found) return lines;
  return [found, ...lines.filter((line) => line.id !== foundLineId)];
}
